import { memo, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { Compass } from 'lucide-react';
import { LiveAvatarRing } from '@components/live';
import { useMyJoinedSpaces } from '@hooks/useMyJoinedSpaces';
import { useHorizontalScrollFade } from '@hooks/useHorizontalScrollFade';
import { useLiveEventsRail } from '@hooks/useLiveEventsRail';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { SpaceAvatarChip } from '@components/SpaceAvatarChip';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { buildEventContextPayload, buildEventParentSpacePayload } from '@lib/analytics/payloads';
import { routes } from '@routes';

const SKELETON_COUNT = 5;
const LOAD_MORE_SKELETON_COUNT = 3;

/**
 * Horizontal carousel of the user's joined spaces (avatar + name).
 *
 * Best practices applied:
 * - Wrapped with memo() to prevent unnecessary re-renders
 * - Named function for better React DevTools debugging
 * - Returns null when data is empty (parent decides fallback)
 * - Skeleton loader while data is in flight
 */
export const SpacesCarousel = memo(function SpacesCarousel() {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitudeTracking();
  const {
    data: spaces,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMyJoinedSpaces();

  // Live rings (spec §5.1). Calling the rail here is deliberate: the carousel is a bounded
  // horizontal list, so its 30s `useNow` tick is acceptable — the Home feed is a sibling that
  // never calls the rail, so it does not re-render (spec §3). React Query dedupes this
  // subscription with the Home pill's and recap's into a single request.
  const { liveSpaceIds, liveEventBySpaceId } = useLiveEventsRail();

  // Ring impressions (spec §9): once per mount per live EVENT, only for rings the
  // paginated carousel actually rendered (a live space on an unloaded page fires
  // nothing until it appears). Set-guarded so 30s ticks / refetch churn no-op,
  // and a ring appearing mid-session (data resolve, phase flip) still fires once.
  const firedRingEventIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const space of spaces) {
      const liveEvent = liveEventBySpaceId.get(space.id);
      if (!liveEvent || firedRingEventIdsRef.current.has(liveEvent.id)) {
        continue;
      }
      firedRingEventIdsRef.current.add(liveEvent.id);
      trackEvent(ANALYTICS_EVENTS.live_surface_displayed, {
        surface: 'carousel_ring',
        ...buildEventContextPayload(liveEvent),
        ...buildEventParentSpacePayload(liveEvent),
      });
    }
  }, [spaces, liveEventBySpaceId, trackEvent]);

  // Stable partition: spaces with a live event float to the front, and BOTH groups keep their
  // original relative order (Array.filter is stable). When nothing is live the input array is
  // returned untouched — zero reorder, and the memo stays referentially stable.
  const orderedSpaces = useMemo(() => {
    if (liveSpaceIds.size === 0) {
      return spaces;
    }
    const live = spaces.filter(space => liveSpaceIds.has(space.id));
    if (live.length === 0) {
      return spaces;
    }
    const rest = spaces.filter(space => !liveSpaceIds.has(space.id));
    return [...live, ...rest];
  }, [spaces, liveSpaceIds]);

  // Sentinel for horizontal infinite scroll. `rootMargin` of 200px on the
  // right edge starts the next-page fetch before the user actually hits
  // the end of the carousel, so loading-more skeletons appear as a
  // continuous extension instead of after a pause.
  const { ref: loadMoreRef, inView: loadMoreInView } = useInView({
    rootMargin: '0px 200px 0px 0px',
  });

  useEffect(() => {
    if (loadMoreInView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [loadMoreInView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Edge-fade pattern (mirrors CommunityList / TopicFilterBar): tracks whether the
  // container is at the start/end so the `scroll-fade-x` mask only fades the side
  // with overflow. Re-evaluates when `spaces.length` changes so a late-loading
  // carousel renders the fade correctly the first time content actually overflows.
  const { scrollRef, fadeStyle } = useHorizontalScrollFade(spaces.length);

  if (isLoading) {
    return (
      <div className="flex gap-3.5 px-2.5 py-1 overflow-x-auto no-scrollbar">
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <div key={`skeleton-${i}`} className="flex flex-col items-center gap-1.5 shrink-0">
            <div className="w-[58px] h-[58px] rounded-full bg-gray-200 animate-pulse" />
            <div className="w-14 h-3 bg-gray-200 rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (spaces.length === 0) return null;

  return (
    <div
      ref={scrollRef}
      className="flex gap-3.5 px-2.5 py-1 overflow-x-auto no-scrollbar scroll-fade-x"
      style={{ WebkitOverflowScrolling: 'touch', ...fadeStyle }}
    >
      {orderedSpaces.map((space, index) => {
        const isLive = liveSpaceIds.has(space.id);
        return (
          <button
            key={space.id}
            type="button"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.spaces_carousel_tap, {
                space_id: space.id,
                space_slug: space.slug,
                // Payload contract (W2-6 decision): `position_in_list` keeps its
                // original meaning — the position the user actually saw (the
                // rendered, live-first order). `original_position` preserves the
                // pre-boost index so the reorder's lift is measurable, and
                // `live` marks ringed taps (spec §9).
                position_in_list: index,
                original_position: spaces.findIndex(s => s.id === space.id),
                live: isLive,
              });
              navigate(routes.community.details(space, { returnTo: '/home' }));
            }}
            className="flex flex-col items-center gap-1.5 shrink-0"
            // The ring is decorative (aria-hidden); the live status rides on the button's accessible
            // name so screen readers announce "live event in {space}" (spec §11).
            aria-label={
              isLive ? `Go to ${space.name}, live event in ${space.name}` : `Go to ${space.name}`
            }
          >
            <SpaceAvatarChip
              name={space.name}
              imageUrl={space.image_url}
              avatarDecorator={
                isLive ? avatar => <LiveAvatarRing active>{avatar}</LiveAvatarRing> : undefined
              }
            />
          </button>
        );
      })}

      {/* Infinite-scroll sentinel: load more pages as the user nears the right edge */}
      {hasNextPage && <div ref={loadMoreRef} className="w-px shrink-0" aria-hidden="true" />}

      {/* Load-more skeletons: keep the carousel width steady while the next page settles */}
      {isFetchingNextPage &&
        Array.from({ length: LOAD_MORE_SKELETON_COUNT }, (_, i) => (
          <div
            key={`load-more-skeleton-${i}`}
            className="flex flex-col items-center gap-1.5 shrink-0"
            aria-hidden="true"
          >
            <div className="w-[58px] h-[58px] rounded-full bg-gray-200 animate-pulse" />
            <div className="w-14 h-3 bg-gray-200 rounded animate-pulse" />
          </div>
        ))}

      {/* Explore more spaces */}
      <button
        type="button"
        onClick={() => {
          trackEvent(ANALYTICS_EVENTS.spaces_carousel_explore_tap, {
            total_spaces_shown: spaces.length,
          });
          navigate(routes.events());
        }}
        className="flex flex-col items-center gap-1.5 shrink-0"
        aria-label="Explore spaces"
      >
        <div className="w-14.5 h-14.5 rounded-full flex items-center justify-center bg-mb-teal/10 outline-2 outline-dashed outline-mb-teal/40">
          <Compass className="w-6 h-6 text-mb-teal" />
        </div>
        <span className="text-xs font-semibold text-mb-teal max-w-20 text-center leading-tight">
          Explore
        </span>
      </button>
    </div>
  );
});
